import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  bulkModerate,
  fetchEventById,
  fetchVideos,
  moderateVideo,
  subscribePendingVideos,
  toggleFavorite,
  updateVideoForceVertical,
  updateVideoRotation,
} from '../services/firebaseService'
import { toaster } from '../components/ui/toaster'
import { useVideoDisplayControls } from '../hooks/useVideoDisplayControls'
import { useModerationSession } from '../hooks/useModerationSession'
import { ModerationToolbar } from '../components/moderation/ModerationToolbar'
import { ModerationGrid } from '../components/moderation/ModerationGrid'
import { WaitingBanner } from '../components/moderation/WaitingBanner'
import { PausedOverlay } from '../components/moderation/PausedOverlay'
import { AiOverrideStrip } from '../components/moderation/AiOverrideStrip'
import { subscribeAiOverrides, acknowledgeAiOverride } from '../services/videoAI'
import { useTranslation } from '../i18n'
import { nextRotation } from '../utils/videoDisplay'
import { useBreakpointValue } from '@chakra-ui/react'

// Moderation screen.
//
// The page is composition and the non-pending archive views; the claiming
// protocol that lets several moderators share one queue lives in
// useModerationSession, and is documented in
// docs/superpowers/specs/2026-09-04-multi-moderator-claiming-design.md

const GRID_KEY = 'crowds_moderation_grid'

function getStoredGrid() {
  try {
    const stored = sessionStorage.getItem(GRID_KEY)
    if (stored) {
      const { rows, cols } = JSON.parse(stored)
      if (rows >= 1 && rows <= 6 && cols >= 1 && cols <= 6) return { rows, cols }
    }
  } catch {
    /* invalid stored grid, fall back to default */
  }
  return { rows: 3, cols: 3 }
}

/** Archive videos are shown through the same tile, so they wear the same shape
 *  as a live slot. They have been seen already, so the watch gate is off. */
function archiveSlot(video) {
  return {
    video,
    claimedAtMs: null,
    decision: video.isModerated ? (video.accepted ? 'accepted' : 'rejected') : null,
    watched: true,
    lost: false,
    lostTo: null,
  }
}

export default function ModerationPage() {
  const { eventId } = useParams()
  const { user, profile, isSysAdmin, hasEventAccess, isModeratorOnly } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    canControl,
    enabled: displayControlsEnabled,
    setEnabled: setDisplayControlsEnabled,
    active: showDisplayControls,
  } = useVideoDisplayControls(eventId)

  // On narrow screens the configured NxN grid is unusable; cap the columns and
  // let the grid scroll vertically instead of squeezing into the viewport.
  const isNarrow = useBreakpointValue({ base: true, md: false })

  const [event, setEvent] = useState(null)
  const [grid, setGrid] = useState(getStoredGrid)
  const [pendingVideos, setPendingVideos] = useState([])
  const [aiOverrides, setAiOverrides] = useState([])
  const [selectedChallenges, setSelectedChallenges] = useState([])
  const [focusedIndex, setFocusedIndex] = useState(-1)

  // Archive views ('all' | 'accepted' | 'rejected') fetch on demand; 'pending'
  // is the live claiming grid.
  const [filterMode, setFilterMode] = useState('pending')
  const [filteredVideos, setFilteredVideos] = useState([])
  const [filterLoading, setFilterLoading] = useState(false)
  const [filterPage, setFilterPage] = useState(0)
  const [bulkLoading, setBulkLoading] = useState(false)

  const gridCapacity = grid.rows * grid.cols

  //--------------------------------------------------------------------------
  // Event + live queue

  useEffect(() => {
    const loadEvent = async () => {
      if (!isSysAdmin && !hasEventAccess(eventId)) {
        toaster.create({ title: t('moderation.noAccess'), status: 'error' })
        navigate('/events', { replace: true })
        return
      }
      setEvent(await fetchEventById(eventId))
    }
    loadEvent()
  }, [eventId, hasEventAccess, navigate, isSysAdmin, t])

  useEffect(() => {
    if (!eventId) return
    const unsub = subscribePendingVideos(eventId, setPendingVideos)
    return () => unsub()
  }, [eventId])

  // Videos the AI rejected after a human approved them. Deliberately a separate
  // subscription from the pending queue: these are already moderated, nothing
  // claims them, and they must not enter the claiming protocol's counts.
  useEffect(() => {
    if (!eventId) return undefined
    return subscribeAiOverrides(eventId, setAiOverrides)
  }, [eventId])

  const handleAcknowledge = useCallback(async (videoId) => {
    try {
      await acknowledgeAiOverride(videoId)
    } catch (err) {
      console.warn('Could not acknowledge override:', err.message)
    }
  }, [])

  const handleAcknowledgeAll = useCallback(async () => {
    await Promise.all(aiOverrides.map((v) => acknowledgeAiOverride(v.id).catch(() => {})))
  }, [aiOverrides])

  //--------------------------------------------------------------------------
  // Claiming session

  const handleModerationWrite = useCallback(
    async (videoId, accepted) => {
      await moderateVideo(videoId, accepted)
      setFilteredVideos((prev) =>
        prev.map((v) => (v.id === videoId ? { ...v, isModerated: true, accepted } : v)),
      )
    },
    [],
  )

  const sessionUser = useMemo(
    () => ({
      uid: user?.uid,
      displayName: profile?.name || user?.displayName || user?.email || '',
      email: user?.email,
    }),
    [user, profile],
  )

  const {
    paused,
    slots,
    available,
    activeModerators,
    serverNow,
    heldCount,
    decide,
    markWatched,
    togglePaused,
    resume,
  } = useModerationSession({
    eventId,
    user: sessionUser,
    pendingVideos,
    capacity: gridCapacity,
    challengeFilter: selectedChallenges,
    onDecide: handleModerationWrite,
  })

  //--------------------------------------------------------------------------
  // Archive views

  useEffect(() => {
    setFilterPage(0)
  }, [filterMode, gridCapacity, selectedChallenges])

  useEffect(() => {
    if (filterMode === 'pending' || !eventId) {
      setFilteredVideos([])
      return
    }
    const load = async () => {
      setFilterLoading(true)
      try {
        setFilteredVideos(
          await fetchVideos(eventId, filterMode === 'all' ? null : filterMode),
        )
      } catch (err) {
        toaster.create({
          title: t('moderation.failedToLoad'),
          description: err.message,
          status: 'error',
        })
      } finally {
        setFilterLoading(false)
      }
    }
    load()
  }, [filterMode, eventId, t])

  const challengeFilteredVideos = useMemo(() => {
    if (selectedChallenges.length === 0) return filteredVideos
    return filteredVideos.filter((v) => selectedChallenges.includes(v.challenge))
  }, [filteredVideos, selectedChallenges])

  const totalFilterPages = Math.max(1, Math.ceil(challengeFilteredVideos.length / gridCapacity))
  const safePage = Math.min(filterPage, totalFilterPages - 1)
  const pagedVideos = useMemo(
    () => challengeFilteredVideos.slice(safePage * gridCapacity, (safePage + 1) * gridCapacity),
    [challengeFilteredVideos, safePage, gridCapacity],
  )

  //--------------------------------------------------------------------------
  // The slots actually on screen

  const displayedSlots = useMemo(() => {
    if (filterMode === 'pending') return slots
    const archive = pagedVideos.map(archiveSlot)
    return [...archive, ...Array.from({ length: Math.max(0, gridCapacity - archive.length) }, () => null)]
  }, [filterMode, slots, pagedVideos, gridCapacity])

  //--------------------------------------------------------------------------
  // Actions

  const handleDecide = useCallback(
    async (videoId, accepted) => {
      if (filterMode === 'pending') {
        await decide(videoId, accepted)
        return
      }
      await handleModerationWrite(videoId, accepted)
    },
    [filterMode, decide, handleModerationWrite],
  )

  const handleFavorite = useCallback(async (videoId, currentFav) => {
    const isFavorite = !currentFav
    setFilteredVideos((prev) => prev.map((v) => (v.id === videoId ? { ...v, isFavorite } : v)))
    await toggleFavorite(videoId, isFavorite)
  }, [])

  const handleRotate = useCallback(async (videoId, currentRotation) => {
    const rotation = nextRotation(currentRotation)
    setFilteredVideos((prev) => prev.map((v) => (v.id === videoId ? { ...v, rotation } : v)))
    await updateVideoRotation(videoId, rotation)
  }, [])

  const handleForceVertical = useCallback(async (videoId, current) => {
    const forceVertical = !current
    setFilteredVideos((prev) => prev.map((v) => (v.id === videoId ? { ...v, forceVertical } : v)))
    await updateVideoForceVertical(videoId, forceVertical)
  }, [])

  // Bulk only ever touches tiles that have earned a decision: watched, and not
  // already decided. In the archive views everything counts as watched.
  const bulkTargets = useMemo(
    () => displayedSlots.filter((s) => s && s.watched && !s.lost && s.decision === null),
    [displayedSlots],
  )

  const handleBulk = useCallback(
    async (accepted) => {
      const ids = bulkTargets.map((s) => s.video.id)
      if (!ids.length) return
      setBulkLoading(true)
      try {
        if (filterMode === 'pending') {
          // Through the session, so each claim is released as its video is decided.
          await Promise.all(ids.map((id) => decide(id, accepted)))
        } else {
          await bulkModerate(ids, accepted)
          const idSet = new Set(ids)
          setFilteredVideos((prev) =>
            prev.map((v) => (idSet.has(v.id) ? { ...v, isModerated: true, accepted } : v)),
          )
        }
        toaster.create({
          title: t('moderation.bulkSuccess', {
            action: t(accepted ? 'moderation.bulkActionAccepted' : 'moderation.bulkActionRejected'),
            count: ids.length,
          }),
          status: 'success',
        })
      } catch (err) {
        toaster.create({
          title: t('moderation.bulkFailed'),
          description: err.message,
          status: 'error',
        })
      } finally {
        setBulkLoading(false)
      }
    },
    [bulkTargets, filterMode, decide, t],
  )

  const toggleChallenge = useCallback((challengeId) => {
    setSelectedChallenges((prev) =>
      prev.includes(challengeId) ? prev.filter((c) => c !== challengeId) : [...prev, challengeId],
    )
  }, [])

  useEffect(() => {
    sessionStorage.setItem(GRID_KEY, JSON.stringify(grid))
  }, [grid])

  //--------------------------------------------------------------------------
  // Keyboard

  const kb = useRef({})
  kb.current = { displayedSlots, cols: grid.cols, handleDecide, handleFavorite, togglePaused }

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return
      const { displayedSlots: current, cols } = kb.current
      const count = current.length

      const act = (fn) =>
        setFocusedIndex((fi) => {
          const slot = fi >= 0 && fi < count ? current[fi] : null
          if (slot) fn(slot)
          return fi
        })

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault()
          setFocusedIndex((p) => (p < 0 ? 0 : Math.min(p + 1, count - 1)))
          break
        case 'ArrowLeft':
          e.preventDefault()
          setFocusedIndex((p) => (p < 0 ? 0 : Math.max(p - 1, 0)))
          break
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex((p) => (p < 0 ? 0 : Math.min(p + cols, count - 1)))
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex((p) => (p < 0 ? 0 : Math.max(p - cols, 0)))
          break
        case 'a':
        case 'A':
          e.preventDefault()
          // Same gate as the buttons: an unwatched video cannot be judged.
          act((s) => (s.watched || s.decision) && kb.current.handleDecide(s.video.id, true))
          break
        case 'x':
        case 'X':
        case 'r':
        case 'R':
          e.preventDefault()
          act((s) => (s.watched || s.decision) && kb.current.handleDecide(s.video.id, false))
          break
        case 'f':
        case 'F':
          e.preventDefault()
          act((s) => kb.current.handleFavorite(s.video.id, s.video.isFavorite))
          break
        case ' ':
          e.preventDefault()
          kb.current.togglePaused()
          break
        case 'Escape':
          setFocusedIndex(-1)
          break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    setFocusedIndex((prev) => {
      if (displayedSlots.length === 0) return -1
      if (prev >= displayedSlots.length) return displayedSlots.length - 1
      return prev
    })
  }, [displayedSlots.length])

  //--------------------------------------------------------------------------

  const challengeMeta = useMemo(() => {
    const map = {}
    ;(event?.challenges || []).forEach((c) => {
      map[c.id] = c
    })
    return map
  }, [event])

  if (!event) {
    return (
      <Flex align="center" justify="center" h="100vh">
        <Text color="var(--mc-text-muted)" fontFamily="'JetBrains Mono', monospace" fontSize="sm">
          {t('moderation.loading')}
        </Text>
      </Flex>
    )
  }

  return (
    // A flat background over the body's glow and grid: this wall is watched for
    // hours, and the grid would show through every empty or letterboxed tile.
    <Flex direction="column" h="100dvh" overflow="hidden" gap={0} bg="var(--mc-bg)">
      <ModerationToolbar
        onBack={() => navigate(isModeratorOnly(eventId) ? '/events' : `/events/${eventId}`)}
        paused={paused}
        onTogglePaused={togglePaused}
        queueCount={available.length}
        filterMode={filterMode}
        onFilterMode={setFilterMode}
        challenges={event.challenges || []}
        selectedChallenges={selectedChallenges}
        onToggleChallenge={toggleChallenge}
        moderators={activeModerators}
        serverNow={serverNow}
        grid={grid}
        onGridChange={setGrid}
        canControl={canControl}
        displayControlsEnabled={displayControlsEnabled}
        onDisplayControlsChange={setDisplayControlsEnabled}
        bulk={{ count: bulkTargets.length, loading: bulkLoading, onBulk: handleBulk }}
      />

      {filterMode !== 'pending' && (
        <WaitingBanner count={heldCount} onReturn={() => setFilterMode('pending')} />
      )}

      <Box flex="1" overflow={isNarrow ? 'auto' : 'hidden'} p="2px" minH={0} position="relative">
        {filterMode !== 'pending' && filterLoading ? (
          <Flex align="center" justify="center" h="100%">
            <Text color="var(--mc-text-muted)">{t('moderation.loadingVideos')}</Text>
          </Flex>
        ) : (
          <>
            <AiOverrideStrip
              overrides={aiOverrides}
              onAcknowledge={handleAcknowledge}
              onAcknowledgeAll={handleAcknowledgeAll}
            />
            <ModerationGrid
              slots={displayedSlots}
              rows={grid.rows}
              cols={grid.cols}
              isNarrow={isNarrow}
              focusedIndex={focusedIndex}
              onFocus={setFocusedIndex}
              onDecide={handleDecide}
              onFavorite={handleFavorite}
              onWatched={markWatched}
              onRotate={handleRotate}
              onForceVertical={handleForceVertical}
              showDisplayControls={showDisplayControls}
              challengeMeta={challengeMeta}
              gateWatch={filterMode === 'pending'}
            />
            {filterMode === 'pending' && paused && (
              <PausedOverlay queueCount={available.length} onStart={resume} />
            )}
          </>
        )}

        {filterMode !== 'pending' && challengeFilteredVideos.length > gridCapacity && (
          <HStack
            position="absolute"
            bottom="6px"
            left="50%"
            transform="translateX(-50%)"
            gap={2}
            px={2}
            py={1}
            borderRadius="full"
            bg="var(--mc-bg-surface)"
            border="1px solid var(--mc-border)"
          >
            <Button
              size="xs"
              variant="ghost"
              disabled={safePage === 0}
              onClick={() => setFilterPage((p) => p - 1)}
            >
              {t('common.prev')}
            </Button>
            <Text fontSize="xs" color="var(--mc-text-muted)" whiteSpace="nowrap">
              {t('moderation.pageInfo', {
                page: safePage + 1,
                total: totalFilterPages,
                count: challengeFilteredVideos.length,
              })}
            </Text>
            <Button
              size="xs"
              variant="ghost"
              disabled={safePage >= totalFilterPages - 1}
              onClick={() => setFilterPage((p) => p + 1)}
            >
              {t('common.next')}
            </Button>
          </HStack>
        )}
      </Box>
    </Flex>
  )
}
